// packages/core/src/reader-theme/reader-theme-data.ts
//
// Node-safe palette of color schemes + font styles + CSS-string builders.
// Shared between @mindex/ui-web (browser side — adds applyScheme/applyStyle
// helpers in src/_lib/reader-theme.js) and @mindex/auth (server side — the
// static share renderer inlines the builders into its <style> block).
//
// Pure data + pure string builders. No DOM access. No imports.

export interface SchemePalette {
  name: string;
  desc: string;
  swatch: readonly string[];
  nav: {
    bg: string; bgElev: string; bgHover: string; bgActive: string;
    text: string; textSoft: string; textMute: string;
    border: string; rule: string;
  };
  reader: {
    bg: string; bgSoft: string;
    text: string; textSoft: string; textMute: string;
    border: string; codeBg: string;
  };
}

export interface StylePreset {
  name: string;
  sample: string;
  display: string; displayStyle: string;
  body: string; mono: string;
  h1Size: string; h1Weight: number;
  h2Size: string; h2Weight: number;
  h3Size: string; h3Weight: number;
  bodySize: string; lineHeight: number;
  letterSpacing: string;
}

export const SCHEMES: Record<string, SchemePalette> = {
  coal: {
    name: "Coal",
    desc: "Dark navigation, light reader. Default.",
    swatch: ['#1a1a1a', '#ffffff'],
    nav:    { bg:'#1a1a1a', bgElev:'#161616', bgHover:'#232323', bgActive:'#2a2a2a',
              text:'#e8e6e1', textSoft:'#8e8a82', textMute:'#5a5751',
              border:'#2a2a2a', rule:'#232323' },
    reader: { bg:'#ffffff', bgSoft:'#f7f7f6',
              text:'#1a1a1a', textSoft:'#5a5a5a', textMute:'#999999',
              border:'#e6e4e0', codeBg:'#f3f1ed' },
  },
  paper: {
    name: "Paper",
    desc: "Light navigation, light reader.",
    swatch: ['#ebebeb', '#ffffff'],
    nav:    { bg:'#ebebeb', bgElev:'#f5f5f5', bgHover:'#e0e0e0', bgActive:'#d6d6d6',
              text:'#1a1a1a', textSoft:'#666666', textMute:'#999999',
              border:'#d0d0d0', rule:'#dcdcdc' },
    reader: { bg:'#ffffff', bgSoft:'#f7f7f6',
              text:'#1a1a1a', textSoft:'#5a5a5a', textMute:'#999999',
              border:'#e6e4e0', codeBg:'#f3f1ed' },
  },
  carbon: {
    name: "Carbon",
    desc: "Dark navigation, dark reader. Night reading.",
    swatch: ['#1a1a1a', '#1c1c1c'],
    nav:    { bg:'#1a1a1a', bgElev:'#161616', bgHover:'#232323', bgActive:'#2a2a2a',
              text:'#e8e6e1', textSoft:'#8e8a82', textMute:'#5a5751',
              border:'#2a2a2a', rule:'#232323' },
    reader: { bg:'#1c1c1c', bgSoft:'#222222',
              text:'#dcd9d2', textSoft:'#9c988e', textMute:'#666258',
              border:'#2a2a2a', codeBg:'#222222' },
  },
  inverse: {
    name: "Inverse",
    desc: "Light navigation, dark reader.",
    swatch: ['#ebebeb', '#1c1c1c'],
    nav:    { bg:'#ebebeb', bgElev:'#f5f5f5', bgHover:'#e0e0e0', bgActive:'#d6d6d6',
              text:'#1a1a1a', textSoft:'#666666', textMute:'#999999',
              border:'#d0d0d0', rule:'#dcdcdc' },
    reader: { bg:'#1c1c1c', bgSoft:'#222222',
              text:'#dcd9d2', textSoft:'#9c988e', textMute:'#666258',
              border:'#2a2a2a', codeBg:'#222222' },
  },
  cream: {
    name: "Cream",
    desc: "Dark navigation, warm cream reader.",
    swatch: ['#1a1a1a', '#f6f0e2'],
    nav:    { bg:'#1a1a1a', bgElev:'#161616', bgHover:'#232323', bgActive:'#2a2a2a',
              text:'#e8e6e1', textSoft:'#8e8a82', textMute:'#5a5751',
              border:'#2a2a2a', rule:'#232323' },
    reader: { bg:'#f6f0e2', bgSoft:'#ede5d2',
              text:'#3a3429', textSoft:'#6e6555', textMute:'#a89c84',
              border:'#dccfb1', codeBg:'#ebe2cc' },
  },
};

export const STYLES: Record<string, StylePreset> = {
  editorial: {
    name: "Editorial",
    sample: "A modern display serif over a clean screen-reading body.",
    display: '"Instrument Serif", Georgia, serif', displayStyle: 'normal',
    body: '"Source Serif 4", Charter, Georgia, serif',
    mono: '"Geist Mono", ui-monospace, monospace',
    h1Size: '46px', h1Weight: 400, h2Size: '22px', h2Weight: 600,
    h3Size: '17px', h3Weight: 600, bodySize: '18px', lineHeight: 1.65,
    letterSpacing: '-0.005em',
  },
  book: {
    name: "Book",
    sample: "Warm and consistent. A single calibrated screen serif throughout.",
    display: 'Literata, Georgia, serif', displayStyle: 'normal',
    body: 'Literata, Georgia, serif',
    mono: '"JetBrains Mono", ui-monospace, monospace',
    h1Size: '36px', h1Weight: 700, h2Size: '22px', h2Weight: 600,
    h3Size: '17px', h3Weight: 600, bodySize: '17.5px', lineHeight: 1.7,
    letterSpacing: '-0.005em',
  },
  newsprint: {
    name: "Newsprint",
    sample: "Editorial display headline above a workhorse text serif.",
    display: 'Fraunces, Georgia, serif', displayStyle: 'normal',
    body: 'Lora, Georgia, serif',
    mono: '"JetBrains Mono", ui-monospace, monospace',
    h1Size: '42px', h1Weight: 600, h2Size: '21px', h2Weight: 600,
    h3Size: '17px', h3Weight: 600, bodySize: '17.5px', lineHeight: 1.7,
    letterSpacing: '-0.01em',
  },
  modern: {
    name: "Modern",
    sample: "Friendly humanist sans for technical writing without coldness.",
    display: '"Bricolage Grotesque", system-ui, sans-serif', displayStyle: 'normal',
    body: '"Plus Jakarta Sans", system-ui, sans-serif',
    mono: '"IBM Plex Mono", ui-monospace, monospace',
    h1Size: '40px', h1Weight: 700, h2Size: '21px', h2Weight: 600,
    h3Size: '16px', h3Weight: 600, bodySize: '16px', lineHeight: 1.65,
    letterSpacing: '-0.01em',
  },
  technical: {
    name: "Technical",
    sample: "IBM Plex pairing — coherent system, neutral, designed for documentation.",
    display: '"IBM Plex Sans", system-ui, sans-serif', displayStyle: 'normal',
    body: '"IBM Plex Sans", system-ui, sans-serif',
    mono: '"IBM Plex Mono", ui-monospace, monospace',
    h1Size: '34px', h1Weight: 600, h2Size: '19px', h2Weight: 600,
    h3Size: '15px', h3Weight: 600, bodySize: '15.5px', lineHeight: 1.65,
    letterSpacing: '-0.005em',
  },
  typewriter: {
    name: "Typewriter",
    sample: "Mono everywhere. iA Writer / Bear-style distraction-free reading.",
    display: '"JetBrains Mono", ui-monospace, monospace', displayStyle: 'normal',
    body: '"JetBrains Mono", ui-monospace, monospace',
    mono: '"JetBrains Mono", ui-monospace, monospace',
    h1Size: '28px', h1Weight: 700, h2Size: '17px', h2Weight: 700,
    h3Size: '14px', h3Weight: 700, bodySize: '14.5px', lineHeight: 1.7,
    letterSpacing: '0',
  },
  hyperlegible: {
    name: "Hyperlegible",
    sample: "Atkinson Hyperlegible — designed for maximum letterform clarity.",
    display: '"Atkinson Hyperlegible", system-ui, sans-serif', displayStyle: 'normal',
    body: '"Atkinson Hyperlegible", system-ui, sans-serif',
    mono: '"JetBrains Mono", ui-monospace, monospace',
    h1Size: '36px', h1Weight: 700, h2Size: '20px', h2Weight: 700,
    h3Size: '16px', h3Weight: 700, bodySize: '16.5px', lineHeight: 1.65,
    letterSpacing: '-0.005em',
  },
};

export function buildSchemeCss(): string {
  const blocks: string[] = [];
  for (const [key, s] of Object.entries(SCHEMES)) {
    blocks.push(`[data-r-scheme="${key}"] {`);
    blocks.push(`  --d-bg: ${s.nav.bg};`);
    blocks.push(`  --d-bg-elev: ${s.nav.bgElev};`);
    blocks.push(`  --d-bg-hover: ${s.nav.bgHover};`);
    blocks.push(`  --d-bg-active: ${s.nav.bgActive};`);
    blocks.push(`  --d-text: ${s.nav.text};`);
    blocks.push(`  --d-text-soft: ${s.nav.textSoft};`);
    blocks.push(`  --d-text-mute: ${s.nav.textMute};`);
    blocks.push(`  --d-border: ${s.nav.border};`);
    blocks.push(`  --d-rule: ${s.nav.rule};`);
    blocks.push(`  --r-bg: ${s.reader.bg};`);
    blocks.push(`  --r-bg-soft: ${s.reader.bgSoft};`);
    blocks.push(`  --r-text: ${s.reader.text};`);
    blocks.push(`  --r-text-soft: ${s.reader.textSoft};`);
    blocks.push(`  --r-text-mute: ${s.reader.textMute};`);
    blocks.push(`  --r-border: ${s.reader.border};`);
    blocks.push(`  --r-code-bg: ${s.reader.codeBg};`);
    blocks.push(`}`);
  }
  return blocks.join('\n');
}

export function buildStyleCss(): string {
  const blocks: string[] = [];
  for (const [key, s] of Object.entries(STYLES)) {
    blocks.push(`[data-r-style="${key}"] {`);
    blocks.push(`  --reader-display: ${s.display};`);
    blocks.push(`  --reader-display-style: ${s.displayStyle};`);
    blocks.push(`  --reader-body: ${s.body};`);
    blocks.push(`  --reader-mono: ${s.mono};`);
    blocks.push(`  --reader-h1-size: ${s.h1Size};`);
    blocks.push(`  --reader-h1-weight: ${s.h1Weight};`);
    blocks.push(`  --reader-h2-size: ${s.h2Size};`);
    blocks.push(`  --reader-h2-weight: ${s.h2Weight};`);
    blocks.push(`  --reader-h3-size: ${s.h3Size};`);
    blocks.push(`  --reader-h3-weight: ${s.h3Weight};`);
    blocks.push(`  --reader-body-size: ${s.bodySize};`);
    blocks.push(`  --reader-line-height: ${s.lineHeight};`);
    blocks.push(`  --reader-letter-spacing: ${s.letterSpacing};`);
    blocks.push(`}`);
  }
  return blocks.join('\n');
}
